/*
 * Copyright (c) CIRROM 2026.
 */

import React from "react";
import Typography from "@mui/material/Typography";
import GppGoodIcon from "@mui/icons-material/GppGood";
import GppMaybeIcon from "@mui/icons-material/GppMaybe";
import GppBadIcon from "@mui/icons-material/GppBad";
import Tooltip from "@mui/material/Tooltip";
import { rampIconColor } from "./helpers/rampColor";
import { EmailStatusSeverity } from "./helpers/normalize";
import classes from "./css/recordReview.module.css";

/*
 * Email deliverability, reconciled with the one ramp.
 *
 * The shared EmailTrafficLight paints these green / amber / red from the MUI
 * semantic palette, which the modal's own comment flagged as an unresolved
 * second traffic light beside the CRM health ramp. It is resolved here rather
 * than there, because the two older modals still depend on it unchanged.
 *
 * The resolution is NOT to drain the colour out. Deliverability is a genuine
 * quality signal about the record itself: a bounce means something is
 * seriously wrong with this contact, never-emailed means the data Cirrom
 * derived has had less corroboration, and a delivered or opened email is
 * evidence the record is accurate. That is a graded "how good is this",
 * which is precisely what the eight-band ramp exists to say.
 *
 * So these take their colour from CRM_SCORE_BANDS - the same ramp, the same
 * single definition, the same swatches the confidence bands use one pane
 * away. One vocabulary, three intensities, no second traffic light. The
 * sentence beside each marker is the words the ramp rule requires, and the
 * shield icons differ in shape, so nothing is carried by colour alone.
 */
const DELIVERABILITY = {
  success: {
    Icon: GppGoodIcon,
    color: rampIconColor("Outstanding"),
    why: "This contact receives email, so Cirrom's data on them is corroborated.",
  },
  warning: {
    Icon: GppMaybeIcon,
    color: rampIconColor("Average"),
    why: "Never emailed, so Cirrom's derived data on this contact is less corroborated.",
  },
  error: {
    Icon: GppBadIcon,
    color: rampIconColor("Critical"),
    why: "A bounce means something is seriously wrong with this contact's data.",
  },
};

export const DeliverabilityFact = (props: {
  severity: EmailStatusSeverity;
  status: string;
}) => {
  const fact = DELIVERABILITY[props.severity];
  if (!fact || !props.status) {
    return null;
  }
  const { Icon, color, why } = fact;
  return (
    /*
     * What deliverability implies about the record is domain knowledge a new
     * steward does not arrive with, so it is one hover away rather than
     * nowhere.
     */
    <Tooltip title={why}>
      <div className={classes.dossierFact}>
        <Icon className={classes.dossierFactIcon} sx={{ color }} />
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          {props.status}
        </Typography>
      </div>
    </Tooltip>
  );
};
